/**
 * React hooks for NoteCache integration
 *
 * These hooks use React 18's useSyncExternalStore for safe concurrent rendering
 * and provide selective subscriptions to minimize re-renders.
 *
 * Auto-fetching: If notes aren't in cache, hooks automatically fetch them from the database.
 * Requests are batched with a small delay to prevent N+1 queries.
 */
import { storageLogger } from '@annote/core/utils/debug';
import { useSyncExternalStore, useCallback, useRef, useEffect } from 'react';
import { Note } from '@annote/types';
import { noteCache, type NoteSelector } from './noteCache';

// Batching mechanism for auto-fetching
const pendingFetchIds = new Set<string>();
let fetchTimer: NodeJS.Timeout | null = null;
let getNotesFunction: ((ids: string[]) => Promise<Note[]>) | null = null;

// Set the getNotes function (called from BrainContext)
export const setGetNotesFunction = (fn: (ids: string[]) => Promise<Note[]>): void => {
  getNotesFunction = fn;
};

// Request notes to be fetched (batched with delay)
// We do a final cache check before calling getNotes to handle race conditions
// where notes arrived during the batching delay
const requestFetch = (ids: string[]): void => {
  if (!getNotesFunction) return;

  ids.forEach((id) => pendingFetchIds.add(id));

  // Clear existing timer
  if (fetchTimer) clearTimeout(fetchTimer);

  // Batch requests with a small delay (20ms)
  fetchTimer = setTimeout(() => {
    if (pendingFetchIds.size > 0 && getNotesFunction) {
      // Final cache check - some notes might have been loaded during the delay
      const stillMissing = Array.from(pendingFetchIds).filter((id) => !noteCache.has(id));
      pendingFetchIds.clear();

      if (stillMissing.length > 0) {
        // getNotes will do one more cache check (for thread safety) then fetch from DB
        getNotesFunction(stillMissing).catch((err) => {
          storageLogger.error('Auto-fetch failed:', err);
        });
      }
    }
  }, 20);
};

/**
 * Hook to subscribe to multiple notes by IDs
 * Auto-fetches missing notes from the database with batched requests.
 *
 * @param ids - Array of note IDs to subscribe to
 * @param selector - Optional selector function to transform each note
 * @returns Array of notes (or undefined for notes not yet loaded)
 */
export function useNotes<T = Note | undefined>(ids: string[], selector?: NoteSelector<T>): T[] {
  // Create stable string representation for dependency tracking
  const idsString = ids.join(',');

  const subscribe = useCallback(
    (callback: () => void) => {
      return noteCache.subscribe((changedIds) => {
        // Check if any of our IDs were changed
        const hasRelevantChange = changedIds.some((changedId) => ids.includes(changedId));
        if (hasRelevantChange) {
          callback();
        }
      });
    },
    [idsString], // Use stable string instead of array
  );

  // Cache the last snapshot to avoid returning a new array identity on every call
  // which can cause useSyncExternalStore to detect spurious changes during
  // synchronous calls and lead to an infinite render loop warning.
  const lastSnapshotRef = useRef<T[] | null>(null);

  const getSnapshot = useCallback(() => {
    // Map over IDs to get notes from cache (preserving undefined for missing notes)
    const notes = ids.map((id) => noteCache.get(id));
    const current: T[] = selector ? notes.map((note) => selector(note)) : (notes as T[]);

    const prev = lastSnapshotRef.current;
    if (prev && prev.length === current.length) {
      let same = true;
      for (let i = 0; i < current.length; i += 1) {
        if (prev[i] !== current[i]) {
          same = false;
          break;
        }
      }
      if (same) return prev;
    }

    lastSnapshotRef.current = current;
    return current;
  }, [idsString, selector]); // Use stable string

  const getServerSnapshot = getSnapshot; // Same for SSR

  const result = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Auto-fetch notes that aren't in cache
  // We batch the request, and getNotes will efficiently check cache again before fetching
  useEffect(() => {
    if (ids.length > 0) {
      const missingIds = ids.filter((id) => !noteCache.has(id));
      if (missingIds.length > 0) {
        requestFetch(missingIds);
      }
    }
  }, [idsString]);

  return result;
}

/**
 * Hook to subscribe to a single note by ID
 * Auto-fetches the note from the database if not in cache.
 *
 * @param id - Note ID to subscribe to
 * @param selector - Optional selector function to transform the note
 * @returns The note (or undefined if not yet loaded)
 */
export const useNote = <T = Note | undefined>(id: string | undefined, selector?: NoteSelector<T>): T => {
  const result = useNotes<T>(id ? [id] : [], selector);
  return result[0] as T;
};

/**
 * Hook that provides cache stats for debugging
 */
export function useNoteCacheStats(): { size: number; suggestedCount: number } {
  const subscribe = useCallback((callback: () => void) => {
    return noteCache.subscribe(() => callback());
  }, []);

  const getSnapshot = useCallback(() => {
    return {
      size: (noteCache as unknown as { size?: () => number }).size?.() || 0,
      suggestedCount: noteCache.getSuggestedIds().length,
    };
  }, []);

  const getServerSnapshot = getSnapshot;

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
